// Turns list tables into cards on small screens (CSS in stylesheets/_mobile.scss).
// Each body cell gets its column header as a data-label so a card reads "Label: value", and:
//   - the title column (data-card-title="Header" on the table, else the first column) heads the card
//   - empty values are hidden
//   - Yes/No values become chips
//   - delete-type actions move to their own quieter row below the main buttons
// Runs on every page load and whenever new tables appear (e.g. filter results replaced by AJAX).

const SKIP = '.fc, .metric-table, .filter-panel, [data-no-cards]';
const DANGER_TEXT = /\b(destroy|delete|void|remove)\b/i;
const YES_NO = { yes: 'chip-yes', true: 'chip-yes', no: 'chip-no', false: 'chip-no' };

const headerLabels = (table) => {
  const row = table.tHead && table.tHead.rows[table.tHead.rows.length - 1];
  if (!row) { return null; }
  const labels = [];
  Array.from(row.cells).forEach((cell) => {
    const text = cell.textContent.replace(/\s+/g, ' ').trim();
    for (let i = 0; i < (cell.colSpan || 1); i += 1) { labels.push(text); }
  });
  return labels.some((label) => label) ? labels : null;
};

const isActionsColumn = (label, cell) => /^actions?$/i.test(label) || cell.classList.contains('actions');

const titleIndex = (table, labels) => {
  const wanted = (table.dataset.cardTitle || '').toLowerCase();
  const named = wanted ? labels.findIndex((l) => l.toLowerCase() === wanted) : -1;
  return named >= 0 ? named : labels.findIndex((l) => l && !/^actions?$/i.test(l));
};

const isEmpty = (cell) => !cell.textContent.trim() && !cell.querySelector('img, svg, box-icon, input, select, a, button');

const chipYesNo = (cell) => {
  if (cell.dataset.chipped || cell.children.length) { return; }
  const kind = YES_NO[cell.textContent.trim().toLowerCase()];
  if (!kind) { return; }
  const chip = document.createElement('span');
  chip.className = 'card-chip ' + kind;
  chip.textContent = cell.textContent.trim();
  cell.textContent = '';
  cell.append(chip);
  cell.dataset.chipped = '1';
};

// Delete-type actions go into their own row so they are hard to tap by accident
const separateDangerActions = (cell) => {
  if (cell.querySelector('.card-danger')) { return; }
  const danger = Array.from(cell.querySelectorAll('a, button, input[type=submit]'))
    // Judged by what the action does (wording or a DELETE request), not its colour: some red buttons only print
    .filter((el) => el.dataset.method === 'delete' || DANGER_TEXT.test(el.textContent || el.value || ''));
  if (danger.length === 0) { return; }
  const row = document.createElement('div');
  row.className = 'card-danger';
  danger.forEach((el) => row.append(el.closest('form') && el.closest('form').parentElement === cell ? el.closest('form') : el));
  cell.append(row);
};

const labelTable = (table) => {
  if (table.closest(SKIP)) { return; }
  const labels = headerLabels(table);
  if (!labels) { return; }
  const title = titleIndex(table, labels);
  table.classList.add('cardable');
  Array.from(table.tBodies).forEach((body) => {
    Array.from(body.rows).forEach((row) => {
      let column = 0;
      Array.from(row.cells).forEach((cell) => {
        const label = labels[column] || '';
        cell.setAttribute('data-label', label);
        if (isActionsColumn(label, cell)) {
          cell.classList.add('card-actions');
          separateDangerActions(cell);
        } else {
          cell.classList.toggle('card-title', column === title);
          cell.classList.toggle('card-empty', isEmpty(cell));
          chipYesNo(cell);
        }
        column += cell.colSpan || 1;
      });
    });
  });
};

const labelAll = () => document.querySelectorAll('table').forEach(labelTable);

let pending = false;
const schedule = () => {
  if (pending) { return; }
  pending = true;
  window.requestAnimationFrame(() => { pending = false; labelAll(); });
};

const ResponsiveTables = {
  start: () => {
    document.addEventListener('turbolinks:load', labelAll);
    new MutationObserver((mutations) => {
      if (mutations.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1))) { schedule(); }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
};

export { ResponsiveTables };
